import {
  autoMapDataFrameColumns,
  AutoMapperColumnJoinType,
  AutoMapperMode,
} from 'analysis-tool/data-frame-creation/column-mapping/ColumnMappingUtil';
import {DraftableStep} from 'analysis-tool/hooks/useDraftSteps';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ConfigDataFrameInfo} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import {PreviewOutput} from 'pipelines/network/PipelineDAO';
import {PipelineStep, PipelineStepData, PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {getOutputDataForView, getParamsFromTable} from 'pipelines/PipelineViewUtils';
import {getInputStepDatas, getOutputStepDatas} from 'pipelines/StepConnectionUtils';

export function stepTypeHasRequiredInputs(stepType: PipelineStepType): boolean {
  return getInputStepDatas(stepType).some((shape) => shape.isRequired);
}

/**
 * Build minimal inputStepDatas for the draft preview step from selected tables.
 * Only populates slots for tables that are selected; the preview API uses these IDs
 * to fetch the input data frames.
 */
export function buildDraftInputStepDatas(
  stepType: PipelineStepType,
  leftTable: PipelineViewGraphQL | null,
  rightTable: PipelineViewGraphQL | null,
  steps: ReadonlyArray<PipelineStep>
): DraftableStep['inputStepDatas'] {
  const inputShapes = getInputStepDatas(stepType);
  const tables = [leftTable, rightTable];
  return inputShapes.flatMap((shape, index): Array<PipelineStepData> => {
    const table = tables[index];
    const params = getParamsFromTable(table, steps);
    if (!params) {
      return [];
    }
    return [
      {
        __typename: 'PipelineStepDataDomainNode',
        id: `draft-input-${shape.systemName}`,
        name: shape.systemName,
        rawDatas: [
          {
            __typename: 'DataFrameDomainNode',
            id: params.dataFrameId,
            name: '',
            columns: [],
          },
        ],
      },
    ];
  });
}

/**
 * Build both `stepInput` and `stepInputParents` for `addOrReplaceAndRunAnalysisStep`
 * from selected input tables.
 */
export function buildStepInputData(
  stepType: PipelineStepType,
  leftTable: PipelineViewGraphQL,
  rightTable: PipelineViewGraphQL,
  steps: ReadonlyArray<PipelineStep>
): {
  stepInput: Record<string, string>;
  stepInputParents: Record<string, Array<{stepId: string; stepDataName: string}>>;
} {
  const inputShapes = getInputStepDatas(stepType);
  const tables = [leftTable, rightTable];
  const stepInput: Record<string, string> = {};
  const stepInputParents: Record<string, Array<{stepId: string; stepDataName: string}>> = {};

  inputShapes.forEach((shape, index) => {
    const params = getParamsFromTable(tables[index], steps);
    if (params) {
      stepInputParents[shape.systemName] = [params.stepInfo];
    }
  });

  // stepInput uses step-type-specific key names rather than shape system names.
  const leftParams = getParamsFromTable(leftTable, steps)!;
  const rightParams = getParamsFromTable(rightTable, steps)!;
  if (stepType === PipelineStepType.JOIN) {
    stepInput.leftDataFrameId = leftParams.dataFrameId;
    stepInput.rightDataFrameId = rightParams.dataFrameId;
  } else if (stepType === PipelineStepType.UNION_DATASETS) {
    stepInput.primaryDataFrameId = leftParams.dataFrameId;
    stepInput.secondaryDataFrameId = rightParams.dataFrameId;
  }

  return {stepInput, stepInputParents};
}

export function buildDataFramesInfos(
  stepType: PipelineStepType,
  leftTable: PipelineViewGraphQL | null,
  rightTable: PipelineViewGraphQL | null,
  steps: ReadonlyArray<PipelineStep>
): Record<string, Array<ConfigDataFrameInfo>> {
  const inputShapes = getInputStepDatas(stepType);
  const tables = [leftTable, rightTable];
  const result: Record<string, Array<ConfigDataFrameInfo>> = {};
  inputShapes.forEach((shape, index) => {
    const table = tables[index];
    if (table) {
      const rawData = getOutputDataForView(table, steps, PipelineStepDataKind.DATASET);
      const {id, ...dataFrameSpec} = rawData;
      result[shape.systemName] = [{id, dataFrameSpec}];
    } else {
      result[shape.systemName] = [];
    }
  });
  return result;
}

/**
 * Auto-map union column relationships by display name (KEEP_SHARED = only columns in both tables).
 * Matches the previous CombineTablesSourceRightSidebar behavior.
 */
export function buildUnionAutoMappedConfig(
  primaryColumns: Array<DataFrameColumnDef>,
  secondaryColumns: Array<DataFrameColumnDef>
): {primaryDataSourceColumns: Array<string>; secondaryDataSourcesColumns: Array<Array<string>>} {
  const {autoMappedTemplateColumns, autoMappedAligningColumns} = autoMapDataFrameColumns(
    primaryColumns,
    secondaryColumns,
    {mode: AutoMapperMode.NAME, columnJoinType: AutoMapperColumnJoinType.KEEP_SHARED}
  );
  return {
    primaryDataSourceColumns: autoMappedTemplateColumns
      .filter((col): col is DataFrameColumnDef => col != null)
      .map((col) => col.systemName),
    secondaryDataSourcesColumns: [
      autoMappedAligningColumns
        .filter((col): col is DataFrameColumnDef => col != null)
        .map((col) => col.systemName),
    ],
  };
}

/**
 * Get the first DATASET output step data name for a step type.
 */
export function getDatasetOutputStepDataName(stepType: PipelineStepType): string | null {
  const outputDatas = getOutputStepDatas(stepType);
  const datasetOutput = outputDatas.find((outputData) => outputData.kind === PipelineStepDataKind.DATASET);
  return datasetOutput?.systemName ?? null;
}

/**
 * Extract data frame columns from a preview output for a given step type.
 * Shared between the column mapping phase (for display) and submit (for allDisplayNames).
 */
export function getPreviewDataFrameColumns(
  previewOutput: PreviewOutput | null,
  stepType: PipelineStepType
): Array<DataFrameColumnDef> | null {
  if (!previewOutput) {
    return null;
  }
  const stepDataName = getDatasetOutputStepDataName(stepType);
  if (!stepDataName) {
    return null;
  }
  const outputStepDatas = previewOutput.outputStepDatas as Record<
    string,
    Array<{columns?: Array<DataFrameColumnDef>}>
  >;
  const dataFrames = outputStepDatas[stepDataName];
  return dataFrames?.[0]?.columns ?? null;
}
